import type { Arc, PieArcDatum } from 'd3-shape';

import { labelPosition } from './label-position.js';
import { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { ELLIPSIS_CHARACTER } from '../../../typography/utils.js';
import { charsFontWidth12pixelsSize400Map } from '../../core/components/text-ellipsis/chars-font-width-map.js';
import {
  calculateWidth,
  TEXT_HEIGHT,
  truncateString,
} from '../../core/components/text-ellipsis/truncate-string.js';
import type { Formatter } from '../../core/types/formatter.js';
import { LABEL_TYPE_OPTION } from '../constants.js';
import type {
  InternalLabel,
  InternalSliceData,
} from '../types/internal/pie-chart.js';
import type { LabelTypeOptions } from '../types/pie-chart.config.js';

export const buildLabel = (
  arc: PieArcDatum<InternalSliceData>,
  path: Arc<unknown, PieArcDatum<InternalSliceData>>,
  valueType: LabelTypeOptions | undefined,
  formatter: Formatter,
  truncationMode: TruncationMode,
  leftCenter: number,
): InternalLabel => {
  const outerRadius = path.outerRadius()(arc);
  const innerRadius = path.innerRadius()(arc);
  const [centroidX, centroidY] = path.centroid(arc);

  const { labelX, labelY } = labelPosition({
    outerRadius,
    innerRadius,
    centroidX,
    centroidY,
  });

  const formattedLabelText = formatLabelText(valueType, formatter, arc.data);

  const availableWidth = leftCenter - Math.abs(labelX);
  const truncated =
    availableWidth > 0
      ? truncateString(
          formattedLabelText,
          availableWidth,
          charsFontWidth12pixelsSize400Map,
          truncationMode,
          'start',
        )
      : '';
  const labelText = truncated === ELLIPSIS_CHARACTER ? '' : truncated;

  const horizontalMeasurements = getHorizontalMeasurements(labelText, labelX);
  const verticalMeasurements = getVerticalMeasurements(labelText, labelY);

  return {
    ...horizontalMeasurements,
    ...verticalMeasurements,
    text: labelText,
  };
};

const formatLabelText = (
  valueType: string | undefined,
  formatter: Formatter,
  data: InternalSliceData,
): string => {
  const labelText =
    valueType === LABEL_TYPE_OPTION.Absolute
      ? formatter(data.value)
      : `${data.relativeValue.toFixed(0)}%`;

  return labelText.trim();
};

const getHorizontalMeasurements = (labelText: string, labelX: number) => {
  const boxWidth = Math.ceil(
    calculateWidth(labelText, charsFontWidth12pixelsSize400Map),
  );
  const textX = labelX + (labelX > 0 ? boxWidth / 2 : -boxWidth / 2);
  const boxX = textX - boxWidth / 2;

  return { boxX, boxWidth, textX };
};

/**
 * Ratio of the visible glyph bounding-box (cap-height + baseline)
 * to the full em-square for the label font (Roboto/DynatraceFlow 12 px).
 * cap-height ≈ 10 px
 */
const GLYPH_HEIGHT_RATIO = 5 / 6;
const TEXT_BOUNDING_BOX_HEIGHT = Math.round(TEXT_HEIGHT * GLYPH_HEIGHT_RATIO);

const getVerticalMeasurements = (text: string, labelY: number) => {
  const boxHeight = text ? TEXT_BOUNDING_BOX_HEIGHT : 0;
  const textY = labelY + (labelY > 0 ? boxHeight : 0);
  const boxY = textY - boxHeight;

  return { boxY, boxHeight, textY };
};
